import { type PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'

import { type RootState } from '~redux/rootReducer'
import { type EmailIdentity, type WorkspaceId } from '~types'

export interface SelectedWorkspace {
  id: WorkspaceId
  name: string | null
  icon: string | null
}

interface OrganizationState {
  selectedOrganization: EmailIdentity | null
  emailIdentities: EmailIdentity[]
  selectedWorkspace: SelectedWorkspace | null
  refreshTrigger: number
}

const initialState: OrganizationState = {
  selectedOrganization: null,
  emailIdentities: [],
  selectedWorkspace: null,
  refreshTrigger: 0,
}

const workspaceFromOrg = (org: EmailIdentity): SelectedWorkspace | null =>
  org.workspace_id
    ? {
        id: org.workspace_id as WorkspaceId,
        name: org.workspace_name,
        icon: org.workspace_icon,
      }
    : null

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setSelectedEIOrganization(state, action: PayloadAction<EmailIdentity>) {
      state.selectedOrganization = action.payload
      // Only orgs belong to a workspace; selecting a non-workspace identity (e.g. "me") keeps the current one.
      const workspace = workspaceFromOrg(action.payload)
      if (workspace) {
        state.selectedWorkspace = workspace
      }
    },
    setEmailIdentities(state, action: PayloadAction<EmailIdentity[]>) {
      state.emailIdentities = action.payload
    },
    setSelectedWorkspace(state, action: PayloadAction<SelectedWorkspace | null>) {
      state.selectedWorkspace = action.payload
    },
    resetOrganizationState(state) {
      state.selectedOrganization = null
      state.emailIdentities = []
      state.selectedWorkspace = null
      state.refreshTrigger = 0
    },
    refreshEmailIdentities(state) {
      state.refreshTrigger += 1
    },
  },
})

export const {
  setSelectedEIOrganization,
  setEmailIdentities,
  setSelectedWorkspace,
  resetOrganizationState,
  refreshEmailIdentities,
} = organizationSlice.actions

// Orgs in the selected workspace, derived (not stored) so it can't go stale after
// rehydration. Excludes non-org identities; falls back to all orgs when none selected.
export const selectWorkspaceOrgs = createSelector(
  (state: RootState) => state?.organization.emailIdentities ?? [],
  (state: RootState) => state?.organization.selectedWorkspace ?? null,
  (emailIdentities, selectedWorkspace): EmailIdentity[] => {
    const orgs = emailIdentities.filter((ei) => ei.organization_id != null)
    if (!selectedWorkspace) return orgs
    return orgs.filter((ei) => ei.workspace_id === selectedWorkspace.id)
  },
)

export default organizationSlice.reducer
